import { alignPopupWithinEditor, getFroalaLicenseKey } from '@tempo-io/fim3-sui-shared';
import { Button } from '@tempo-io/sui-button';
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PLUGINS, loadPlugins } from './plugins';
import {
  EditorActions,
  EditorContainer,
  EditorSkeleton,
  EditorWrapper,
} from './styles/editor.styles';
import type { RichTextEditorProps } from './types';
import { BrowserDOMPurify } from './utils/dompurify';

// CSS imports for Froala
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/froala_style.min.css';

// Dynamic import of Froala editor
const FroalaEditorComponent = React.lazy(() => import('react-froala-wysiwyg'));

// Base Froala configuration
const createFroalaConfig = (placeholder?: string, autoFocus?: boolean) => ({
  key: getFroalaLicenseKey(),
  attribution: false,
  autofocus: !!autoFocus,
  heightMin: 100,
  heightMax: 450,
  placeholderText: placeholder || '',
  zIndex: 3700,
  toolbarButtons: {
    moreText: {
      buttons: [
        'bold',
        'italic',
        'underline',
        'strikeThrough',
        'subscript',
        'superscript',
        'fontSize',
        'textColor',
        'backgroundColor',
        'inlineClass',
        'clearFormatting',
      ],
      buttonsVisible: 3,
      align: 'left',
    },
    moreParagraph: {
      buttons: [
        'alignLeft',
        'alignCenter',
        'alignRight',
        'alignJustify',
        'formatOL',
        'formatUL',
        'paragraphFormat',
        'lineHeight',
        'outdent',
        'indent',
        'quote',
      ],
      buttonsVisible: 3,
      align: 'left',
    },
    moreRich: {
      buttons: [
        'insertLink',
        'insertImage',
        'insertTable',
        'emoticons',
        'specialCharacters',
        'insertHR',
      ],
      buttonsVisible: 0,
      align: 'left',
    },
    moreMisc: {
      buttons: ['undo', 'redo', 'selectAll', 'help'],
      buttonsVisible: 2,
      align: 'right',
    },
    showMoreButtons: true,
  },
  linkStyles: {
    'fr-strong': 'Thick'
  },
  listAdvancedTypes: true,
  linkAlwaysBlank: true,
  imageUpload: false,
  imageEditButtons: [
    'imageReplace',
    'imageAlign',
    'imageRemove',
    '|',
    'imageLink',
    'linkOpen',
    'linkEdit',
    'linkRemove',
    '-',
    'imageDisplay',
    'imageAlt',
    'imageSize',
  ],
  language: 'en_ca',
  wordPasteModal: false,
});

export const RichTextEditor = React.memo<RichTextEditorProps>(
  ({
    value,
    onChange,
    onSave,
    onCancel,
    plugins = DEFAULT_PLUGINS,
    placeholder,
    autoFocus = false,
    sx,
  }) => {
    const { t } = useTranslation('translation');
    const [pluginsLoaded, setPluginsLoaded] = useState(false);
    const [internalValue, setInternalValue] = useState(value);

    // Load plugins dynamically
    useEffect(() => {
      let isMounted = true;

      const loadEditorPlugins = async () => {
        try {
          await loadPlugins(plugins);
          if (isMounted) {
            setPluginsLoaded(true);
          }
        } catch {
          // Failed plugins are simply unavailable in the toolbar.
          if (isMounted) {
            setPluginsLoaded(true);
          }
        }
      };

      loadEditorPlugins();

      return () => {
        isMounted = false;
      };
    }, [plugins]);

    // Sync internal value with prop
    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const handleChange = useCallback(
      (newValue: string) => {
        setInternalValue(newValue);
        onChange(newValue);
      },
      [onChange],
    );

    const handleSave = useCallback(() => {
      const sanitizedValue = BrowserDOMPurify.sanitize(internalValue);
      onSave?.(sanitizedValue);
    }, [internalValue, onSave]);

    const handleCancel = useCallback(() => {
      setInternalValue(value);
      onCancel?.();
    }, [value, onCancel]);

    const froalaConfig = useMemo(
      () => ({
        ...createFroalaConfig(placeholder, autoFocus),
        events: {
          'paste.beforeCleanup': (html: string | Node) => BrowserDOMPurify.sanitize(html),
          // biome-ignore lint/suspicious/noExplicitAny: Froala editor instance typed as any
          'popups.show.link.insert'(this: any) {
            alignPopupWithinEditor(this, 'link.insert', 'insertLink');
          },
          // biome-ignore lint/suspicious/noExplicitAny: Froala editor instance typed as any
          'popups.show.link.edit'(this: any) {
            alignPopupWithinEditor(this, 'link.edit');
          },
        },
      }),
      [placeholder, autoFocus],
    );

    if (!pluginsLoaded) {
      return (
        <EditorContainer sx={sx}>
          <EditorSkeleton>Loading editor...</EditorSkeleton>
        </EditorContainer>
      );
    }

    return (
      <EditorContainer sx={sx}>
        <EditorWrapper>
          <Suspense fallback={<EditorSkeleton></EditorSkeleton>}>
            <FroalaEditorComponent
              tag="textarea"
              model={internalValue}
              config={froalaConfig}
              onModelChange={handleChange}
              data-testid="rich-text-editor"
            />
          </Suspense>
        </EditorWrapper>

        {(onSave || onCancel) && (
          <EditorActions>
            {onSave && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                data-testid="rich-text-editor-save"
              >
                {t('ideaManagement.general.save')}
              </Button>
            )}
            {onCancel && (
              <Button
                variant="link"
                size="sm"
                onClick={handleCancel}
                data-testid="rich-text-editor-cancel"
              >
                {t('common.cancel')}
              </Button>
            )}
          </EditorActions>
        )}
      </EditorContainer>
    );
  },
);
